// Cart bundle behaviours for natively nested lines (parent_id add-ons):
//   1. Collapsible parent line — a Show/Hide toggle expands/collapses its children.
//   2. Quantity — changing a PARENT (bundle) line's quantity updates the parent and
//      all its children in ONE /cart/update.js request. Single (non-bundle) lines are
//      left to the theme's own one-call handling. A light fallback reconciles children
//      if a parent quantity ever changes without going through the interceptor.
// Everything is delegated + driven by cart:update so it survives the cart's re-renders.

/* ------------------------------------------------------------------ *
 * 1. Collapsible bundle lines
 * ------------------------------------------------------------------ */
(function () {
  if (window.__cartBundleToggle) return;
  window.__cartBundleToggle = true;

  /** @type {Set<string>} keys of collapsed parent lines */
  const collapsed = new Set();

  const esc = (s) =>
    window.CSS && CSS.escape ? CSS.escape(s) : String(s).replace(/["\\\]]/g, '\\$&');

  function apply() {
    document.querySelectorAll('[data-cc-bundle-toggle]').forEach((btn) => {
      const key = btn.getAttribute('data-cc-bundle-toggle');
      if (!key) return;
      const isCollapsed = collapsed.has(key);
      btn.setAttribute('aria-expanded', String(!isCollapsed));
      const scope = btn.closest('cart-items-component') || document;
      scope.querySelectorAll('[data-parent-key="' + esc(key) + '"]').forEach((row) => {
        row.classList.toggle('cart-items__table-row--collapsed', isCollapsed);
      });
    });
  }

  document.addEventListener('click', (e) => {
    const target = e.target instanceof Element ? e.target : null;
    const btn = target ? target.closest('[data-cc-bundle-toggle]') : null;
    if (!btn) return;
    e.preventDefault();
    const key = btn.getAttribute('data-cc-bundle-toggle');
    if (!key) return;
    if (collapsed.has(key)) collapsed.delete(key);
    else collapsed.add(key);
    apply();
  });

  document.addEventListener('cart:update', () => requestAnimationFrame(apply));

  if (document.readyState !== 'loading') apply();
  else document.addEventListener('DOMContentLoaded', apply);
})();

/* ------------------------------------------------------------------ *
 * 2. Bundle quantity — single-call parent + children update
 * ------------------------------------------------------------------ */
(function () {
  if (window.__cartBundleSync) return;
  window.__cartBundleSync = true;

  let busy = false;

  /** Loading overlay on the cart + disabled checkout while the request is in flight. */
  function setBusy(on) {
    if (busy === on) return;
    busy = on;
    document.documentElement.classList.toggle('cart-bundle-busy', on);
  }

  function sectionIds() {
    const ids = [];
    document.querySelectorAll('cart-items-component[data-section-id]').forEach((el) => {
      const id = el.dataset.sectionId;
      if (id && ids.indexOf(id) === -1) ids.push(id);
    });
    return ids;
  }

  /** @param {any} item @returns {string | null} */
  function parentKeyOf(item) {
    const pr = item && item.parent_relationship;
    if (!pr) return null;
    return pr.parent_key || (pr.parent && pr.parent.key) || null;
  }

  /**
   * Update many lines in one request (keyed by line key) and re-render the cart.
   * @param {Record<string, number>} updates
   */
  function applyUpdates(updates) {
    const keys = Object.keys(updates || {});
    if (!keys.length) return Promise.resolve();
    setBusy(true);
    return fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        updates,
        sections: sectionIds().join(','),
        sections_url: window.location.pathname,
      }),
    })
      .then((r) => r.json())
      .then((cart) => {
        // Feeds the theme's cart:update handler (it morphs from detail.data.sections).
        document.dispatchEvent(
          new CustomEvent('cart:update', {
            bubbles: true,
            detail: {
              resource: cart,
              sourceId: 'cart-bundle-sync',
              data: {
                source: 'cart-bundle-sync',
                itemCount: cart.item_count,
                sections: cart.sections,
              },
            },
          })
        );
      })
      .catch(() => {})
      .then(() => setBusy(false));
  }

  /* ---- Primary: intercept a parent line's quantity change (capture phase, before
     the theme's own handler) and update parent + children in a single request. ---- */
  /** @type {Record<string, number> | null} */
  let pending = null;
  /** @type {ReturnType<typeof setTimeout> | null} */
  let timer = null;

  document.addEventListener(
    'quantity-selector:update',
    (e) => {
      const detail = /** @type {any} */ (e).detail || {};
      const t = e.target;
      if (!(t instanceof Element)) return;
      const row = t.closest('.cart-items__table-row');
      const key = row && row.getAttribute('data-key');
      if (!key) return;

      const qty = detail.quantity;
      if (typeof qty !== 'number' || qty <= 0) return; // 0 = remove → let the theme handle it

      const scope = row.closest('cart-items-component') || document;
      const kids = Array.from(scope.querySelectorAll('[data-parent-key]')).filter(
        (r) => r.getAttribute('data-parent-key') === key && r.getAttribute('data-key')
      );
      if (!kids.length) return; // not a bundle parent → let the theme handle this single line

      // Take over from the theme's /cart/change.js and batch parent + children.
      e.stopImmediatePropagation();
      /** @type {Record<string, number>} */
      const p = pending || {};
      pending = p;
      p[key] = qty;
      kids.forEach((r) => {
        const ck = r.getAttribute('data-key');
        if (ck) p[ck] = qty;
      });

      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        const u = pending;
        pending = null;
        applyUpdates(u || {});
      }, 300);
    },
    true // capture — runs before the theme's document-level listener
  );

  /* ---- Fallback: if a parent quantity ever changes some other way, reconcile the
     children on the next cart:update. Uses the cart carried by the event (no extra
     fetch) and ignores our own updates. ---- */
  /** @param {any} cart */
  function reconcileFrom(cart) {
    if (!cart || !Array.isArray(cart.items)) return;
    const items = /** @type {any[]} */ (cart.items);
    /** @type {Record<string, number>} */
    const qtyByKey = {};
    items.forEach((it) => {
      qtyByKey[it.key] = it.quantity;
    });
    /** @type {Record<string, number>} */
    const updates = {};
    let any = false;
    items.forEach((it) => {
      const pk = parentKeyOf(it);
      if (pk && qtyByKey[pk] != null && it.quantity !== qtyByKey[pk]) {
        updates[it.key] = qtyByKey[pk];
        any = true;
      }
    });
    if (any) applyUpdates(updates);
  }

  document.addEventListener('cart:update', (e) => {
    const detail = /** @type {any} */ (e).detail;
    if (detail && detail.sourceId === 'cart-bundle-sync') return; // our own update
    if (!document.querySelector('.cart-items__nested-line')) return; // no bundle in the cart
    const cart = detail && detail.resource;
    const items = cart && Array.isArray(cart.items) ? /** @type {any[]} */ (cart.items) : null;
    if (items && items.some((it) => parentKeyOf(it))) {
      reconcileFrom(cart);
    } else {
      fetch('/cart.js', { headers: { Accept: 'application/json' } })
        .then((r) => r.json())
        .then(reconcileFrom)
        .catch(() => {});
    }
  });
})();

/* ------------------------------------------------------------------ *
 * 3. "Show more details" toggle for long line-item property lists
 *    A cover line can carry many properties (Color/Brand/Model/dimensions/
 *    Add-ons + variant options); the list is clamped and this toggle expands it.
 *    Driven here (not a CSS checkbox) so it never puts a form control inside the
 *    cart <form> and survives the cart's section re-renders. Each toggle is scoped
 *    to its OWN line by key — clicking one never affects another.
 * ------------------------------------------------------------------ */
(function () {
  if (window.__cartPropsToggle) return;
  window.__cartPropsToggle = true;

  /** @type {Set<string>} keys of lines whose property list is expanded */
  const expanded = new Set();

  function apply() {
    document.querySelectorAll('[data-cc-props-toggle]').forEach((btn) => {
      const key = btn.getAttribute('data-cc-props-toggle');
      if (!key) return;
      const isExpanded = expanded.has(key);
      btn.setAttribute('aria-expanded', String(isExpanded));
      const wrap = btn.closest('.cart-items__variants-wrapper');
      if (wrap) wrap.classList.toggle('cart-items__variants-wrapper--expanded', isExpanded);
    });
  }

  document.addEventListener('click', (e) => {
    const target = e.target instanceof Element ? e.target : null;
    const btn = target ? target.closest('[data-cc-props-toggle]') : null;
    if (!btn) return;
    e.preventDefault();
    e.stopPropagation();
    const key = btn.getAttribute('data-cc-props-toggle');
    if (!key) return;
    if (expanded.has(key)) expanded.delete(key);
    else expanded.add(key);
    apply();
  });

  document.addEventListener('cart:update', () => requestAnimationFrame(apply));

  if (document.readyState !== 'loading') apply();
  else document.addEventListener('DOMContentLoaded', apply);
})();
